import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Save,
  User as UserIcon,
  Mail,
  Globe,
  Image as ImageIcon,
  Loader2,
} from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import type { RootState } from "@/store";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ImageUpload from "@/components/common/ImageUpload";
import { useProfile, useUpdateProfile } from "@/services/profileService";
import { ACCEPTABLE_LANGUAGES } from "@/constants/languages";
import { privateRequest } from "@/utils/api";

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const navigate = useNavigate();

  const { data: profileData, isLoading } = useProfile();
  const updateProfileMutation = useUpdateProfile();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    locale: currentLanguage,
    avatar: "",
    bio: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  // Separate state for avatar preview (not saved until form submission)
  const [avatarPreview, setAvatarPreview] = useState<string>("");
  // Ref to store pending avatar change to avoid calling setState during render
  const pendingAvatarChangeRef = useRef<string | null>(null);
  // Counter to trigger useEffect when avatar changes
  const [avatarChangeTrigger, setAvatarChangeTrigger] = useState(0);

  // Load profile data when available
  useEffect(() => {
    if (profileData) {
      setFormData({
        name: profileData.name || "",
        email: profileData.email || "",
        locale: profileData.locale || currentLanguage,
        avatar: profileData.avatar || "",
        bio: profileData.bio || "",
      });
      setAvatarPreview(profileData.avatar || "");
      pendingAvatarChangeRef.current = null;
    }
  }, [profileData, currentLanguage]);

  // Process pending avatar changes after render completes
  // This useEffect runs when avatarChangeTrigger changes
  useEffect(() => {
    if (pendingAvatarChangeRef.current !== null) {
      const avatarUrl = pendingAvatarChangeRef.current;
      // Clear the ref first
      pendingAvatarChangeRef.current = null;
      // Update preview state after render completes
      // This prevents calling setState during ImageUpload's render
      setAvatarPreview(avatarUrl);
    }
  }, [avatarChangeTrigger]);

  // Avatar upload handler - upload file to server
  const handleAvatarUpload = useCallback(
    async (files: File[]): Promise<string[]> => {
      const file = files[0];
      if (!file) return [];

      setIsUploadingAvatar(true);
      try {
        // Upload file to backend endpoint which stores file and returns URL
        const uploadFormData = new FormData();
        uploadFormData.append("file", file);
        const res = await privateRequest.post("/upload", uploadFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // Expect response.data.data.url (single) or response.data.data.urls (array)
        const data = res.data?.data || {};
        let uploadedUrl = "";
        if (Array.isArray(data.urls) && data.urls.length > 0) {
          uploadedUrl = data.urls[0];
        } else if (data.url) {
          uploadedUrl = data.url;
        }

        // Update preview state directly after upload completes
        // Use Promise.resolve().then() to defer the update and avoid render issues
        if (uploadedUrl) {
          // Defer state update to avoid calling setState during render
          Promise.resolve().then(() => {
            setAvatarPreview(uploadedUrl);
          });
        }

        // Return the URL - ImageUpload will use this for its internal state
        return uploadedUrl ? [uploadedUrl] : [];
      } catch (err) {
        console.error("Avatar upload failed:", err);
        setIsUploadingAvatar(false);
        throw err;
      } finally {
        setIsUploadingAvatar(false);
      }
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSuccessMessage("");

    // Basic validation
    if (!formData.name.trim()) {
      setErrors({ name: t("admin.profile.name_required", "Name is required") });
      return;
    }

    if (!formData.email.trim()) {
      setErrors({
        email: t("admin.profile.email_required", "Email is required"),
      });
      return;
    }

    // Use avatarPreview if available, otherwise use formData.avatar
    const avatarToSend = avatarPreview || formData.avatar || undefined;

    // Validate avatar - check if it's base64 and too long, or if it's a valid URL
    if (avatarToSend) {
      // Check if it's a base64 data URL (starts with data:)
      if (avatarToSend.startsWith("data:")) {
        if (avatarToSend.length > 500) {
          setErrors({
            avatar: t(
              "admin.profile.avatar_too_large",
              "Avatar image is too large. Please upload a smaller image or use a URL."
            ),
          });
          return;
        }
      }
      // If it's a URL, validate it
      else if (
        !avatarToSend.startsWith("http://") &&
        !avatarToSend.startsWith("https://") &&
        !avatarToSend.startsWith("/")
      ) {
        setErrors({
          avatar: t(
            "admin.profile.avatar_invalid",
            "Please provide a valid image URL or upload an image."
          ),
        });
        return;
      }
    }

    try {
      await updateProfileMutation.mutateAsync({
        name: formData.name,
        email: formData.email,
        locale: formData.locale,
        avatar: avatarToSend,
        bio: formData.bio || undefined,
      });

      // After successful save, sync formData.avatar and avatarPreview
      const savedAvatar = avatarToSend || formData.avatar || "";
      setFormData((prev) => ({ ...prev, avatar: savedAvatar }));
      // Sync avatarPreview to clear "unsaved changes" indicator
      setAvatarPreview(savedAvatar);
      // Clear any pending changes
      pendingAvatarChangeRef.current = null;
      // Show success message
      setSuccessMessage(
        t("admin.profile.update_success", "Profile updated successfully")
      );
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error: unknown) {
      console.error("Error updating profile:", error);
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as {
          response?: {
            data?: {
              errors?: Record<string, string | string[]>;
              message?: string;
            };
          };
        };
        if (apiError.response?.data?.errors) {
          // Convert array errors to string
          const formattedErrors: Record<string, string> = {};
          Object.entries(apiError.response.data.errors).forEach(
            ([key, value]) => {
              if (Array.isArray(value)) {
                formattedErrors[key] = value[0] || "";
              } else {
                formattedErrors[key] = value;
              }
            }
          );
          setErrors(formattedErrors);
        } else {
          setErrors({
            general:
              apiError.response?.data?.message ||
              t("admin.profile.update_error", "Failed to update profile"),
          });
        }
      } else {
        setErrors({
          general: t("admin.profile.update_error", "Failed to update profile"),
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-600 dark:text-gray-400">
          {t("common.loading", "Loading...")}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t("admin.profile.title", "Profile Settings")}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t("admin.profile.subtitle", "Update your profile information")}
          </p>
        </div>
      </motion.div>

      {/* Success Message */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-800 dark:text-green-200"
        >
          {successMessage}
        </motion.div>
      )}

      {/* Error Message */}
      {errors.general && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200"
        >
          {errors.general}
        </motion.div>
      )}

      {/* Profile Form */}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserIcon className="w-5 h-5" />
                  {t("admin.profile.basic_info", "Basic Information")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    {t("admin.profile.name", "Name")} *
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder={t(
                      "admin.profile.name_placeholder",
                      "Enter your name"
                    )}
                    className={errors.name ? "border-red-500" : ""}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600">
                      {Array.isArray(errors.name)
                        ? errors.name[0]
                        : errors.name}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {t("admin.profile.email", "Email")} *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder={t(
                      "admin.profile.email_placeholder",
                      "Enter your email"
                    )}
                    className={errors.email ? "border-red-500" : ""}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-600">
                      {Array.isArray(errors.email)
                        ? errors.email[0]
                        : errors.email}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="locale" className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    {t("admin.profile.locale", "Language")}
                  </Label>
                  <select
                    id="locale"
                    value={formData.locale}
                    onChange={(e) =>
                      setFormData({ ...formData, locale: e.target.value })
                    }
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {ACCEPTABLE_LANGUAGES.map((lang) => (
                      <option key={lang} value={lang}>
                        {lang === "en" ? "English" : "Français"}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio">{t("admin.profile.bio", "Bio")}</Label>
                  <Textarea
                    id="bio"
                    value={formData.bio}
                    onChange={(e) =>
                      setFormData({ ...formData, bio: e.target.value })
                    }
                    placeholder={t(
                      "admin.profile.bio_placeholder",
                      "Tell us about yourself"
                    )}
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Avatar Section */}
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  {t("admin.profile.avatar", "Profile Picture")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isUploadingAvatar && (
                  <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t("admin.profile.uploading_avatar", "Uploading avatar...")}
                  </div>
                )}
                <ImageUpload
                  label={t(
                    "admin.profile.avatar_label",
                    "Upload your profile picture"
                  )}
                  value={avatarPreview || formData.avatar}
                  onChange={(value) => {
                    // Defer onChange to avoid calling setState during render
                    // This is a workaround for ImageUpload calling onChange during render
                    const avatarUrl = Array.isArray(value)
                      ? value[0] || ""
                      : value || "";
                    if (avatarUrl) {
                      // Only update if not a blob URL (temporary preview)
                      if (!avatarUrl.startsWith("blob:")) {
                        // Use a ref and deferred update to prevent render issues
                        pendingAvatarChangeRef.current = avatarUrl;
                        // Trigger update after render completes
                        Promise.resolve().then(() => {
                          setAvatarChangeTrigger((prev) => prev + 1);
                        });
                      }
                    }
                  }}
                  onUpload={handleAvatarUpload}
                  multiple={false}
                  disabled={isUploadingAvatar}
                />
                {errors.avatar && (
                  <p className="text-sm text-red-600">
                    {Array.isArray(errors.avatar)
                      ? errors.avatar[0]
                      : errors.avatar}
                  </p>
                )}
                {(avatarPreview || formData.avatar) && !isUploadingAvatar && (
                  <div className="mt-4">
                    <img
                      src={avatarPreview || formData.avatar}
                      alt="Avatar preview"
                      className="w-32 h-32 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                    />
                  </div>
                )}
                {avatarPreview && avatarPreview !== formData.avatar && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                    {t(
                      "admin.profile.avatar_unsaved",
                      "Changes will be saved when you click 'Save Changes'"
                    )}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Actions */}
          <div className="space-y-6">
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-md">
              <CardHeader>
                <CardTitle>{t("admin.profile.actions", "Actions")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  type="submit"
                  disabled={
                    updateProfileMutation.isPending || isUploadingAvatar
                  }
                  className="w-full bg-blue-600 hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateProfileMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t("common.saving", "Saving...")}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {t("common.save", "Save Changes")}
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    navigate(`/${currentLanguage}/admin/dashboard`)
                  }
                  className="w-full"
                >
                  {t("common.cancel", "Cancel")}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

// Display name for debugging
Profile.displayName = "Profile";

export default Profile;
